'use client';

import { HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useHelp } from '@/contexts/help-context';
import type { HelpTopicId } from '@/lib/help/content';

interface HelpButtonProps {
  /** Topic to deep-link to when clicked. */
  topicId: HelpTopicId;
  /** Optional visible label shown next to the icon. */
  label?: string;
  /** Accessible label when no visible label is provided. */
  ariaLabel?: string;
  className?: string;
}

/**
 * An inline "?" help trigger placed next to a feature. Clicking it opens the
 * help side-drawer deep-linked to the given topic, so users can read the docs
 * in context without navigating away.
 *
 * @example
 * <HelpButton topicId="rules" ariaLabel="How rules work" />
 */
export function HelpButton({ topicId, label, ariaLabel, className }: HelpButtonProps) {
  const { openHelp } = useHelp();

  return (
    <button
      type="button"
      onClick={() => openHelp(topicId)}
      aria-label={ariaLabel ?? label ?? 'Open help'}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        label ? 'text-sm px-1' : 'p-0.5',
        className
      )}
    >
      <HelpCircle className="h-4 w-4" />
      {label && <span>{label}</span>}
    </button>
  );
}
